import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { HttpStatusCode } from "axios";
import clsx from "clsx";

import { MANAGER_ROLE } from "../../../../consts/member";

import { errorResponse } from "../../../../libs/common";
import { convertFullDateTimeToWithoutYearSecond } from "../../../../libs/dateFormat";

import { useUserContext } from "../../../../contexts/userContext";

import {
  patchEtcSatisfactionReply,
  postEtcSatisfactionReply,
} from "../../../../services/etcServices";
import {
  GetManagerHotColdVoteSatisfactionResponse,
  ManagerVote,
  VoteSatisfactionInfo,
  getManagerHotColdVote,
  getManagerHotColdVoteSatisfaction,
} from "../../../../services/managerHotColdVoteServices";
import { getPeriodString } from "../../../../services/hotColdVoteServices";

import useSimplePopup from "../../../../hooks/useSimplePopup";
import useFullScreenLoading from "../../../../hooks/useFullScreenLoading";
import { RefreshIcon } from "../../../../hooks/useStoreHeader";

import StickyHeader from "../../../../components/Layout/StickyHeader";
import HorizontalRule from "../../../../components/Rule/Horizontal";
import CommonProfile from "../../../../components/MyInfo/Profile";
import StarScore from "../../../../components/Talk/StarScore";
import CommonTextArea from "../../../../components/TextArea";
import CommonButton from "../../../../components/Button";
import CommonPopup from "../../../../components/Popup";

type Props = {};

export default function ManagerHotColdVoteFeedback(props: Props) {
  const navigate = useNavigate();
  const params = useParams();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const loadingComponent = useFullScreenLoading({ isLoading });

  const [simplePopupComponent, { open }] = useSimplePopup({});
  const [user] = useUserContext();

  const [feedbackList, setFeedbackList] = useState<
    Partial<GetManagerHotColdVoteSatisfactionResponse>
  >({});
  const [vote, setVote] = useState<ManagerVote>();

  const loadFeedbackList = async (voteId: number) => {
    try {
      setIsLoading(true);
      const res = await getManagerHotColdVoteSatisfaction({ voteId });
      if (res.status === HttpStatusCode.Ok) {
        setFeedbackList(res.data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const getTitle = () => {
    const buildingFloorName = vote?.buildingFloorName ?? "";
    const buildingSpaceName = vote?.buildingSpaceName ?? "";
    const votePeriod = getPeriodString(vote?.votePeriod);

    return `${buildingFloorName} ${buildingSpaceName} ${votePeriod}`;
  };

  const loadVote = async (voteId: number) => {
    try {
      const res = await getManagerHotColdVote({ voteId });
      if (res.status === HttpStatusCode.Ok) {
        setVote(res.data);
      }
    } catch (e: any) {
      errorResponse(
        e,
        open,
        "투표 정보 조회를 실패했습니다.\n관리자에게 문의해주세요."
      );
    }
  };

  const loadAll = async (voteId: number) => {
    setIsLoading(true);
    Promise.allSettled([loadVote(voteId), loadFeedbackList(voteId)]).finally(
      () => setIsLoading(false)
    );
  };

  const handleRefresh = () => {
    loadAll(Number(params.id));
  };

  useEffect(() => {
    if (!params.id) return;

    loadAll(Number(params.id));
  }, [params.id]);

  useEffect(() => {
    if (user.isLoading) return;

    if (user.role?.includes(MANAGER_ROLE.USER)) {
      navigate("/view");
    }
  }, [user.isLoading]);

  return (
    <div className="px-5 flex flex-col">
      {loadingComponent}
      <StickyHeader
        title="더워요 추워요"
        onBackClick={() => {
          const queryParams = new URLSearchParams();
          params.id && queryParams.append("voteId", params.id);
          vote?.buildingSpaceId &&
            queryParams.append("buildingSpaceId", vote?.buildingSpaceId);

          navigate(
            `/manager/manager-hot-cold-vote/detail?${queryParams.toString()}`
          );
        }}
        rightContent={
          <div onClick={handleRefresh}>
            <RefreshIcon />
          </div>
        }
      />

      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-3 pt-[30px]">
          <span className="text-[18px] font-medium">사용자 만족도</span>
          <div className="flex flex-col gap-2.5 text-[26px] font-medium">
            <div className="flex flex-col leading-[38px] gap-0.5">
              <span>{vote?.buildingName ?? ""}</span>
              <span className="text-brand-primary-magenta">{getTitle()}</span>
            </div>
            <div className="text-sm font-normal">{`내 위치 : ${
              vote?.location ?? ""
            }`}</div>
          </div>
        </div>

        <HorizontalRule className="bg-brand-neutral-05" margin="0" />

        <div className="flex flex-col gap-3 text-base font-semibold">
          작성된 피드백
          <div className="flex gap-[5px] font-semibold">
            {`평균 평점 :`}
            {feedbackList.average && (
              <>
                <StarScore score={feedbackList.average ?? 0} size="M" />{" "}
                <span className="flex items-center text-sm font-medium text-[#999999]">{`(${feedbackList.average}점)`}</span>
              </>
            )}
          </div>
          {vote?.feedback && (
            <div className="border border-brand-neutral-20 rounded-[3px] text-sm font-normal break-keep p-3 text-brand-neutral-90">
              {vote.feedback}
            </div>
          )}
        </div>

        {!isLoading && (
          <div className="flex flex-col gap-[15px]">
            {feedbackList.satisfaction?.map((feedback) => (
              <ManagerHotColdVoteFeedbackItem
                key={feedback.voteItemId}
                feedback={feedback}
                setIsLoading={setIsLoading}
                open={open}
                onRefresh={handleRefresh}
              />
            ))}
          </div>
        )}
      </div>
      {simplePopupComponent}
    </div>
  );
}

export type ManagerHotColdVoteFeedbackItemProps = {
  feedback: VoteSatisfactionInfo;
  className?: string;
  setIsLoading?: (isLoading: boolean) => void;
  open?: (message: string) => void;
  onRefresh?: () => void;
};

export const ManagerHotColdVoteFeedbackItem = ({
  feedback,
  className,
  setIsLoading,
  open,
  onRefresh,
}: ManagerHotColdVoteFeedbackItemProps) => {
  const [comment, setComment] = useState<string>(feedback.reply?.comment ?? "");
  const [popup, setPopup] = useState<"commentPost" | "commentModify" | "">("");

  const isReply = Boolean(feedback.reply);

  const isDisabled =
    comment.length < 10 ||
    (isReply ? feedback.reply?.comment === comment : false);

  return (
    <div
      key={feedback.etcSatisfactionId}
      className={clsx(
        "flex flex-col gap-2.5 pb-5 border-b border-brand-neutral-05",
        className
      )}
    >
      <div className="flex flex-col gap-2.5">
        <div className="flex justify-between">
          <div className="flex gap-2.5">
            <CommonProfile src={feedback.createdByThumbnail} />
            <div className="flex flex-col gap-[5px]">
              <StarScore score={feedback.satisfaction ?? 0} />
              <div className=" text-sm text-brand-neutral-85 font-semibold">
                {feedback.createdByName ?? ""}
              </div>
            </div>
          </div>
          <div className="text-[#999999] text-xs font-medium">
            {convertFullDateTimeToWithoutYearSecond(feedback.createdAt).replace(
              ".",
              "/"
            )}
          </div>
        </div>
        <div className="break-words break-keep pl-[50px] pb-[10px] text-base font-normal text-brand-neutral-90">
          {feedback.comment}
        </div>
      </div>
      <div className="pl-[50px]">
        <div className="bg-brand-main-page-background h-[150px] rounded-[5px] p-[15px]">
          <div className="relative">
            <CommonTextArea
              className="w-full"
              value={comment}
              onChange={(e) => {
                setComment(e.target.value);
              }}
              inputClassName="text-sm w-full rounded-[3px]"
              placeholder={"이용자 후기에 대한 댓글을 남겨주세요.\n(10자 이상)"}
            />
            <CommonButton
              variant={"primary"}
              disabled={isDisabled}
              className={clsx(
                "absolute bottom-2.5 right-2.5 px-4 py-[8.5px] text-sm"
              )}
              onClick={() => {
                if (comment.length < 10) {
                  open?.("댓글은 10자 이상 입력해주세요.");
                  return;
                }

                if (isReply) {
                  setPopup("commentModify");
                } else {
                  setPopup("commentPost");
                }
              }}
            >{`${isReply ? "수정" : "등록"}`}</CommonButton>
          </div>
        </div>
      </div>

      <CommonPopup
        isOpen={popup === "commentPost"}
        onClose={() => setPopup("")}
        padding={0}
      >
        <div className="flex justify-center break-keep text-lg font-medium p-7 leading-[30px]">
          댓글을 등록 하시겠습니까?
        </div>
        <div className="flex">
          <CommonButton
            variant="secondary"
            className="rounded-none flex-1 h-bottom-button"
            onClick={() => setPopup("")}
          >
            취소
          </CommonButton>
          <CommonButton
            className="rounded-none flex-1 h-bottom-button"
            onClick={async () => {
              console.log("feedback", feedback, comment);
              setIsLoading?.(true);
              try {
                const res = await postEtcSatisfactionReply(
                  feedback.voteItemId.toString(),
                  { comment, serviceName: "hotColdVote" }
                );

                if (res.status === HttpStatusCode.Ok) {
                  open?.("댓글을 등록했습니다.");
                }
              } catch (e: any) {
                if (open) {
                  const message =
                    "댓글 등록을 실패했습니다.\n관리자에게 문의해주세요.";
                  errorResponse(e, open, message);
                } else {
                  console.log(e);
                }
              } finally {
                setIsLoading?.(false);
                onRefresh?.();
                setPopup("");
              }
            }}
          >
            확인
          </CommonButton>
        </div>
      </CommonPopup>

      <CommonPopup
        isOpen={popup === "commentModify"}
        onClose={() => setPopup("")}
        padding={0}
      >
        <div className="flex justify-center break-keep text-lg font-medium p-7 leading-[30px]">
          댓글을 수정 하시겠습니까?
        </div>
        <div className="flex">
          <CommonButton
            variant="secondary"
            className="rounded-none flex-1 h-bottom-button"
            onClick={() => setPopup("")}
          >
            취소
          </CommonButton>
          <CommonButton
            className="rounded-none flex-1 h-bottom-button"
            onClick={async () => {
              if (!feedback.reply?.etcSatisfactionId) return;

              setIsLoading?.(true);
              try {
                const res = await patchEtcSatisfactionReply(
                  feedback.reply.etcSatisfactionId.toString(),
                  { comment }
                );

                if (res.status === HttpStatusCode.Ok) {
                  open?.("댓글을 수정했습니다.");
                }
              } catch (e: any) {
                if (open) {
                  const message =
                    "댓글 수정을 실패했습니다.\n관리자에게 문의해주세요.";
                  errorResponse(e, open, message);
                } else {
                  console.log(e);
                }
              } finally {
                setIsLoading?.(false);
                onRefresh?.();
                setPopup("");
              }
            }}
          >
            확인
          </CommonButton>
        </div>
      </CommonPopup>
    </div>
  );
};
